import type { NurseryChild, NurseryChildPayload } from './types';

export interface NurseryFormState {
  name: string;
  guardianName: string;
  gender: NurseryChild['gender'] | '';
  bloodGroup: NurseryChild['bloodGroup'] | '';
  dateOfBirth: string;
  dateOfBirthYear: string;
  dateOfBirthMonth: string;
  dateOfBirthDay: string;
  address: string;
  state: string;
  district: string;
  pinCode: string;
  doctor: string;
  diagnosis: string;
  operation: string;
  weight: string;
  babyStatus: NurseryChild['babyStatus'] | '';
  deliveryMode: NurseryChild['deliveryMode'] | '';
  admissionDate: string;
  dischargeDate: string;
}

export const emptyNurseryForm: NurseryFormState = {
  name: '', guardianName: '', gender: '', bloodGroup: '', dateOfBirth: '', dateOfBirthYear: '',
  dateOfBirthMonth: '', dateOfBirthDay: '', address: '', state: 'West Bengal (WB)', district: '',
  pinCode: '', doctor: '', diagnosis: '', operation: '', weight: '', babyStatus: '', deliveryMode: '',
  admissionDate: '', dischargeDate: '',
};

const toDateTimeLocal = (value: string | null) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime()) || date.getTime() === 0) return '';
  const offset = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
};

export const nurseryChildToForm = (child: NurseryChild): NurseryFormState => ({
  name: child.name,
  guardianName: child.guardianName ?? '',
  gender: child.gender,
  bloodGroup: child.bloodGroup ?? '',
  dateOfBirth: toDateTimeLocal(child.dateOfBirth),
  dateOfBirthYear: child.dateOfBirthYear ?? '',
  dateOfBirthMonth: child.dateOfBirthMonth ?? '',
  dateOfBirthDay: child.dateOfBirthDay ?? '',
  address: child.address,
  state: child.state,
  district: child.district,
  pinCode: child.pinCode ?? '',
  doctor: child.doctor.id,
  diagnosis: child.diagnosis ?? '',
  operation: child.operation ?? '',
  weight: child.weight ?? '',
  babyStatus: child.babyStatus,
  deliveryMode: child.deliveryMode ?? '',
  admissionDate: toDateTimeLocal(child.admissionDate),
  dischargeDate: toDateTimeLocal(child.dischargeDate),
});

const nullable = (value: string) => value.trim() || null;
const isoOrNull = (value: string) => value ? new Date(value).toISOString() : null;

export const validateNurseryForm = (form: NurseryFormState): Record<string, string> => {
  const errors: Record<string, string> = {};
  if (!form.name.trim()) errors.name = 'Name of the Patient is required';
  if (!form.gender) errors.gender = 'Gender is required';
  if (!form.address.trim()) errors.address = 'Address is required';
  if (!form.state) errors.state = 'State is required';
  if (!form.district.trim()) errors.district = 'District is required';
  if (!form.doctor) errors.doctor = 'Under Doctor is required';
  if (!form.babyStatus) errors.babyStatus = 'Status of Baby is required';
  if (form.pinCode && !/^\d{4,12}$/.test(form.pinCode.trim())) errors.pinCode = 'Enter a valid pin number';
  if (form.dateOfBirth && form.dischargeDate && new Date(form.dischargeDate) < new Date(form.dateOfBirth)) {
    errors.dischargeDate = 'Discharge cannot be before birth';
  }
  if (form.admissionDate && form.dischargeDate && new Date(form.dischargeDate) < new Date(form.admissionDate)) {
    errors.dischargeDate = 'Discharge cannot be before admission';
  }
  return errors;
};

export const nurseryFormToPayload = (form: NurseryFormState): NurseryChildPayload => ({
  name: form.name.trim(),
  guardianName: nullable(form.guardianName),
  gender: form.gender as NurseryChild['gender'],
  bloodGroup: form.bloodGroup || null,
  dateOfBirth: isoOrNull(form.dateOfBirth),
  dateOfBirthYear: nullable(form.dateOfBirthYear),
  dateOfBirthMonth: nullable(form.dateOfBirthMonth),
  dateOfBirthDay: nullable(form.dateOfBirthDay),
  address: form.address.trim(),
  state: form.state,
  district: form.district.trim(),
  pinCode: nullable(form.pinCode),
  doctor: form.doctor,
  diagnosis: nullable(form.diagnosis),
  operation: nullable(form.operation),
  weight: nullable(form.weight),
  babyStatus: form.babyStatus as NurseryChild['babyStatus'],
  deliveryMode: form.deliveryMode || null,
  admissionDate: isoOrNull(form.admissionDate),
  dischargeDate: isoOrNull(form.dischargeDate),
});
